import { NavLink } from 'react-router-dom';
import { contracts, type Permission } from '@immo/api-contract';
import { useWorkspaceRoutes } from '@/lib/workspace-path';
import { useTranslations } from '@/i18n/use-translations';
import { PORTAL_NAMES } from '@/lib/portal-names';
import { usePortalConnections } from '@/pages/settings/use-portal-connection';
import { WarningTriangleIcon } from './icons';
import { mayCall } from '@/lib/permissions/route-gate';

/**
 * App-wide notice that a portal stopped accepting the workspace's grant or access
 * (IMM-1057.1, IMM-246.2): one line per expired portal. Only for members who can
 * renew it — gated on the permission of the route the link leads to acting on — and
 * it cannot be dismissed: it goes when the connection is renewed, changed or removed.
 */
export function PortalReconnectBanner({
  workspaceId,
  permissions,
}: {
  readonly workspaceId: string;
  readonly permissions: ReadonlyArray<Permission>;
}) {
  const t = useTranslations('pages.settings.portals.banner');
  const routes = useWorkspaceRoutes();
  const canManage = mayCall(permissions, contracts.portalConnections.start);

  // Skip the fetch entirely for members who couldn't act on it anyway.
  const { connections } = usePortalConnections(canManage ? workspaceId : undefined);

  const expired = (connections ?? []).filter((connection) => connection.status === 'EXPIRED');

  if (!canManage || expired.length === 0) return null;

  return (
    <>
      {expired.map((connection) => (
        <div
          key={connection.portal}
          role="status"
          className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 bg-warning-200 px-6 py-3 text-center text-sm font-medium text-warning-700"
        >
          <span className="inline-flex items-center gap-1.5">
            <WarningTriangleIcon className="h-4 w-4 shrink-0" aria-hidden />
            {connection.portal === 'IMMOSCOUT24'
              ? t('title')
              : t('openimmo-title', { portal: PORTAL_NAMES[connection.portal] })}
          </span>
          <NavLink
            to={routes.settingsPortals()}
            className="underline underline-offset-2 rounded-sm hover:text-warning-700 focus-visible:outline-none focus-visible:shadow-focus"
          >
            {connection.portal === 'IMMOSCOUT24' ? t('renew-cta') : t('openimmo-cta')}
          </NavLink>
        </div>
      ))}
    </>
  );
}
